import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { pruneDisplayLabel, pruneTimelineExclude } from '@/features/schema/format'
import type { EntityFormState } from '@/features/schema/types'
import type { MergeStrategy, ValidationFinding } from '@/services/api/schemas'
import { ConventionsEditor } from './ConventionsEditor'
import { DialogValidationFindings } from './DialogValidationFindings'
import { PropertyEditor } from './PropertyEditor'

export interface EntityFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isEditing: boolean
  form: EntityFormState
  onFormChange: (form: EntityFormState) => void
  saving: boolean
  onSave: () => void
  findings?: ValidationFinding[] | null
}

/**
 * Controlled create/edit dialog for an entity type. Pure — it emits the next
 * form value and a save intent; the container owns the actual persistence.
 */
export function EntityFormDialog({
  open,
  onOpenChange,
  isEditing,
  form,
  onFormChange,
  saving,
  onSave,
  findings,
}: EntityFormDialogProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[85dvh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-foreground">
            {t(`schemaAgent.editor.entityDialog.${isEditing ? 'titleEdit' : 'titleNew'}`)}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('schemaAgent.editor.entityDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-1">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">{t('schemaAgent.editor.label')}</Label>
            <Input
              value={form.label}
              onChange={e => onFormChange({ ...form, label: e.target.value })}
              placeholder={t('schemaAgent.editor.entityDialog.labelPlaceholder')}
              className="bg-muted border-border"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.description')}
            </Label>
            <Textarea
              value={form.description}
              onChange={e => onFormChange({ ...form, description: e.target.value })}
              placeholder={t('schemaAgent.editor.entityDialog.descriptionPlaceholder')}
              className="bg-muted border-border h-[120px] resize-none overflow-y-auto"
            />
          </div>

          <PropertyEditor
            properties={form.properties}
            onChange={props => {
              // Drop overrides that no longer match a declared property name.
              onFormChange({
                ...form,
                properties: props,
                timeline_exclude: pruneTimelineExclude(form.timeline_exclude, props),
                display_label: pruneDisplayLabel(form.display_label, props),
              })
            }}
          />

          {/* Entity-resolution merge config — distinct from the temporal-geo
              conventions below; governs how unique-property duplicates collapse. */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.entityDialog.mergeStrategy')}
            </Label>
            <Select
              value={form.merge_strategy}
              onValueChange={value =>
                onFormChange({ ...form, merge_strategy: value as MergeStrategy })
              }
            >
              <SelectTrigger className="max-md:w-full bg-muted border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border z-[250]">
                <SelectItem value="all">{t('schemaAgent.editor.entityDialog.mergeAll')}</SelectItem>
                <SelectItem value="any">{t('schemaAgent.editor.entityDialog.mergeAny')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('schemaAgent.editor.entityDialog.mergeHelp')}
            </p>
          </div>

          <ConventionsEditor
            kind="entity"
            properties={form.properties}
            trackable={form.trackable}
            onTrackableChange={v => onFormChange({ ...form, trackable: v })}
            timelineExclude={form.timeline_exclude}
            onTimelineExcludeChange={v => onFormChange({ ...form, timeline_exclude: v })}
            displayLabel={form.display_label}
            onDisplayLabelChange={v => onFormChange({ ...form, display_label: v })}
          />
        </div>

        <DialogValidationFindings findings={findings} />

        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border"
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button variant="solid" onClick={onSave} loading={saving}>
            {isEditing
              ? t('schemaAgent.editor.saveChanges')
              : t('schemaAgent.editor.entityDialog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
